import { BaseUITools } from "@/uiControls/base/baseUITools";
import {
  createNumericSliderUI,
  NumericSliderUI,
} from "@/uiControls/base/ui/numericSliderUI";
import { SwitchUI } from "@/uiControls/base/ui/switchUI";
import { CheckButtonGroupUI } from "@/uiControls/base/ui/checkButtonGroupUI";
import { cameraUtils } from "@/utils/cameraUtils";
import { globalConfig, globalManager } from "@/utils/globalConfig";
import { sceneUtils } from "@/utils/sceneUtils";
import { getI18nText } from "@/utils/commonFunc";
import { SkyboxGroupUI } from "../base/ui/skyboxGroupUI";
import { SkyboxSelectUI } from "../base/ui/skyboxSelectUI";
import { HeliumPreTrainedModel } from "@huggingface/transformers";

// 模型辅助工具，扩展多视窗，与Scene无绑定关系，单独使用
export class ScenePanelTools extends BaseUITools {
  private gridControl: SwitchUI | null = null; // 网格
  private ortControl: CheckButtonGroupUI | null = null; // 控制器
  private dragSpeedControl: NumericSliderUI | null = null; // 拖拽速度
  private scaleSpeedControl: NumericSliderUI | null = null; // 缩放速度
  private dempingControl: NumericSliderUI | null = null; // 阻尼
  private isActive: boolean = false; // 是否激活
  private animateControlDom: SwitchUI | null = null; // 动画
  private skyboxGroup: SkyboxGroupUI | null = null; // 环境贴图
  private skyboxPathSelect: SkyboxSelectUI | null = null; // 天空盒路径

  constructor(id: string) {
    super(id, false);
    this.init();
  }

  init() {
    const panelContainerElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-container",
    });
    const titleElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-title",
    });
    const titleLeftElement = this.ui.createElement({
      tag: "div",
      textContent: "场景设置",
      className: "panel-setting-title-left",
      i18nKey: "title.sceneSettings",
    });
    const titleRightElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-title-right",
    });
    const resetButtonElement = this.ui.createButton({
      textContent: "重置",
      className: ["btn-item", "btn-text-item", "inherit-font-size"],
      i18nKey: "buttons.reset",
    });
    resetButtonElement.addEventListener("click", () => {
      this.onReset();
      this.setActive(false);
    });
    titleRightElement.appendChild(resetButtonElement);

    const closeButtonElement = this.ui.createButton({
      iconName: "cuowu1",
      isIconOnly: true,
      className: ["btn-item", "btn-icon-item", "inherit-font-size"],
      // i18nKey: 'buttons.close'
    });
    closeButtonElement.addEventListener("click", () => {
      this.onClose();
    });
    titleRightElement.appendChild(closeButtonElement);

    titleElement.appendChild(titleLeftElement);
    titleElement.appendChild(titleRightElement);
    panelContainerElement.appendChild(titleElement);

    const viewElement = this.initViewSettingControl();
    panelContainerElement.appendChild(viewElement);

    const ortViewElement = this.initOrtViewControl();
    panelContainerElement.appendChild(ortViewElement);

    const environmentViewElement = this.initEnvironmentControl();
    panelContainerElement.appendChild(environmentViewElement);

    this.appendChild("panelContainer", panelContainerElement);
  }
  initViewSettingControl() {
    const viewElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-view",
    });
    const viewTitleElement = this.ui.createElement({
      tag: "div",
      textContent: "视窗设置",
      className: "panel-setting-view-title",
      i18nKey: "label.viewWindow",
    });
    viewElement.appendChild(viewTitleElement);

    const gridControl = new SwitchUI({
      id: "grid-control",
      containerClass: "panel-setting-item",
      label: "网格",
      checked: globalConfig.sceneParams.grid,
      labelClass: "panel-setting-item-label",
      switchContainerClass: "panel-setting-item-content",
      i18nKey: "label.grid",
    });
    this.gridControl = gridControl;
    this.gridControl.onChange((value) => {
      console.log("gridControl onChange", value);
      cameraUtils.onGridVisibleChange(value);
    });
    viewElement.appendChild(gridControl.getContainer());

    const animateControl = new SwitchUI({
      id: "animate-control",
      containerClass: "panel-setting-item",
      label: "动画预览",
      checked: globalConfig.sceneParams.isAnimation,
      labelClass: "panel-setting-item-label",
      switchContainerClass: "panel-setting-item-content",
      i18nKey: "label.openAnimation",
    });
    animateControl.getContainer().classList.add("panel-setting-animation-item");
    this.animateControlDom = animateControl;
    this.animateControlDom.onChange((value) => {
      console.log("animateControlDom onChange", value);
      sceneUtils.setModelAnimation(value);
    });
    viewElement.appendChild(animateControl.getContainer());

    const ortControl = this.ui.createCheckButtonGroup({
      label: "控制器",
      labelClass: "panel-setting-item-label",
      containerClass: "panel-setting-item",
      groupClass: "panel-setting-item-content",
      items: [
        {
          id: "orbitView",
          title: "控制球",
          value: "orbit",
          i18nKey: "label.orbitView",
        },
        {
          id: "fspView",
          title: "第一人称",
          value: "fps",
          i18nKey: "label.fpsView",
        },
      ],
      direction: "horizontal",
      defaultActiveId: "orbitView",
      i18nKey: "label.controler",
    });
    this.ortControl = ortControl;
    this.ortControl.onButtonClick((item, index) => {
      console.log("ortControl onChange", item, index);
      cameraUtils.onCameraModeChange(item.value);
    });
    viewElement.appendChild(ortControl.getContainer());

    return viewElement;
  }

  initOrtViewControl() {
    const viewElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-view",
    });
    const viewTitleElement = this.ui.createElement({
      tag: "div",
      textContent: "操作设置",
      className: "panel-setting-view-title",
      i18nKey: "label.operationSettings",
    });
    viewElement.appendChild(viewTitleElement);

    const dragSpeedControl = createNumericSliderUI({
      id: "drag-speed-control",
      containerClass: "panel-setting-item",
      label: "拖拽速度",
      showValue: false,
      value: globalConfig.sceneParams.dragSpeed,
      min: 0.1,
      max: 10,
      step: 0.1,
      labelClass: "panel-setting-item-label",
      sliderContainerClass: "panel-setting-item-content",
      i18nKey: "label.dragSpeed",
    });
    this.dragSpeedControl = dragSpeedControl;
    this.dragSpeedControl.onChange((value) => {
      console.log("dragSpeedControl onChange", value);
      cameraUtils.onDragSpeedChange(value);
    });
    viewElement.appendChild(dragSpeedControl.getContainer());

    const scaleSpeedControl = createNumericSliderUI({
      id: "scale-speed-control",
      containerClass: "panel-setting-item",
      label: "缩放速度",
      showValue: false,
      value: globalConfig.sceneParams.scaleSpeed,
      min: 0.1,
      max: 10,
      step: 0.1,
      labelClass: "panel-setting-item-label",
      sliderContainerClass: "panel-setting-item-content",
      i18nKey: "label.scaleSpeed",
    });
    this.scaleSpeedControl = scaleSpeedControl;
    this.scaleSpeedControl.onChange((value) => {
      console.log("scaleSpeedControl onChange", value);
      cameraUtils.onScaleSpeedChange(value);
    });
    viewElement.appendChild(scaleSpeedControl.getContainer());

    const dempingControl = createNumericSliderUI({
      id: "demping-control",
      containerClass: "panel-setting-item",
      label: "阻尼",
      showValue: false,
      value: globalConfig.sceneParams.demping,
      min: 0.01,
      max: 1,
      step: 0.001,
      labelClass: "panel-setting-item-label",
      sliderContainerClass: "panel-setting-item-content",
      i18nKey: "label.demping",
    });
    this.dempingControl = dempingControl;
    this.dempingControl.onChange((value) => {
      console.log("dempingControl onChange", value);
      cameraUtils.onDampingChange(value);
    });
    viewElement.appendChild(dempingControl.getContainer());
    return viewElement;
  }

  initEnvironmentControl() {
    const viewElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-view",
    });
    const viewTitleElement = this.ui.createElement({
      tag: "div",
      textContent: "环境设置",
      className: "panel-setting-view-title",
      i18nKey: "label.environmentSettings",
    });
    viewElement.appendChild(viewTitleElement);

    const skyboxContainer = this.ui.createElement({
      tag: "div",
      className: "panel-setting-item",
    });
    // const skyboxTitleElement = this.ui.createElement({
    //     tag: 'div',
    //     textContent: '环境贴图',
    //     className: 'panel-setting-item-label',
    //     i18nKey: 'label.environmentMap',
    // });
    // skyboxContainer.appendChild(skyboxTitleElement);
    // const skyboxSelectControl = this.ui.createElement({
    //     tag: 'div',
    //     className: 'panel-setting-item-content',
    // });
    // skyboxSelectControl.classList.add('panel-setting-group');

    const skyboxGroup = new SkyboxGroupUI({
      label: "环境贴图",
      labelClass: "panel-setting-item-label",
      labelPosition: "top",
      containerClass: "panel-setting-item",
      gap: "1rem",
      groupClass: "panel-setting-item-content",
      items: [
        {
          title: "无贴图",
          value: "none",
          i18nKey: "lightSettings.skybox.none",
          backgroundImage: "./images/panel/skybox_none.png",
        },
        {
          title: "环境贴图2",
          value: "skybox",
          i18nKey: "lightSettings.skybox.skybox",
          backgroundImage: "./images/panel/skybox_sky.png",
        },
      ],
      direction: "horizontal",
      defaultActiveId: "skybox",
      i18nKey: "label.environmentMap",
    });
    skyboxGroup.onButtonClick((item, index) => {
      console.log("skyboxGroup onChange", item, index);
      sceneUtils.setSkyboxSettingsEnabled(item.value !== "none");
    });
    this.skyboxGroup = skyboxGroup;
    skyboxContainer.appendChild(skyboxGroup.getContainer());
    viewElement.appendChild(skyboxContainer);
    return viewElement;
  }
  setupEventHandlers() {
    window.addEventListener("event.panelTabChange", (event: Event) => {
      console.log("panelTabChange", event);
      const detail = (event as CustomEvent).detail;
      this.setActive(detail === "scene");
      this.update();
    });
  }
  onReset() {
    console.log("重置");
    globalConfig.sceneParams.grid = false;
    globalConfig.sceneParams.ort = "orbitView";
    globalConfig.sceneParams.dragSpeed = 1;
    globalConfig.sceneParams.scaleSpeed = 1;
    globalConfig.sceneParams.demping = 1;
    globalConfig.sceneParams.isAnimation = true;
    globalConfig.lightSettings.skyboxSettings.enabled = true;
    globalConfig.lightSettings.skyboxSettings.url =
      globalConfig.lightSettings.defalutSettings.skyboxSettings.url;
    this.gridControl.setValue(globalConfig.sceneParams.grid);
    globalConfig.mouseMovementMode = "orbit";
    this.ortControl.setActiveButton(globalConfig.mouseMovementMode + "View");
    this.dragSpeedControl.setValue(globalConfig.sceneParams.dragSpeed);
    this.scaleSpeedControl.setValue(globalConfig.sceneParams.scaleSpeed);
    this.dempingControl.setValue(globalConfig.sceneParams.demping);
    this.animateControlDom.setChecked(globalConfig.sceneParams.isAnimation);
    this.skyboxGroup.setActiveButton("skybox");
    this.skyboxPathSelect?.setValue(
      globalConfig.lightSettings.skyboxSettings.url
    );
    sceneUtils.setSkyboxSettingsEnabled(
      globalConfig.lightSettings.skyboxSettings.enabled
    );
    sceneUtils.setSkyboxSettingsUrl(
      globalConfig.lightSettings.skyboxSettings.url
    );
  }
  onClose() {
    console.log("关闭");
    globalManager.closeRightToolsBar();
  }
  update() {
    if (this.isActive) {
      // console.log("scene panel tools update");
      this.animateControlDom.setValue(globalConfig.sceneParams.isAnimation);
    }
  }
  setActive(active: boolean) {
    this.isActive = active;
  }

  setSkyboxEnabled(enabled: boolean) {
    if (this.skyboxGroup) {
      this.skyboxGroup.setActiveButton(enabled ? "skybox" : "none");
    }
  }

  setSkyboxUrl(url: string) {
    this.skyboxPathSelect?.setValue(url);
  }

  /**
   * 获取相机模式控制器
   */
  public getOrtControl(): CheckButtonGroupUI | null {
    return this.ortControl;
  }

  /**
   * 获取网格开关控制器
   */
  public getGridControl(): SwitchUI | null {
    return this.gridControl;
  }
}
